import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import _ from "lodash";
import { apiConstants } from "../../common/constants";
import { ASSIGNEE_TTL_MS, cachedGet } from "./escalationApiCache";
import { prettyStatus } from "./analyticsLabels";
import { COURIER_LIST_QUERY_PAYLOAD } from "../../common/ProshipFormComponents";
import { useGetAllCourierPartnersCachedQuery } from "../../redux/commonApiCalls";
import { DRILLDOWN_FLAG_KEYS, UNASSIGNED_OPTION, defaultDateRange } from "./escalationFilterUtils";

const initialDateRangeFor = (initialFilters, defaultDays) => {
    if (!initialFilters) return defaultDateRange(defaultDays);
    if (initialFilters.dateFrom || initialFilters.dateTo) {
        return {
            startDate: initialFilters.dateFrom ? new Date(initialFilters.dateFrom) : null,
            endDate: initialFilters.dateTo ? new Date(initialFilters.dateTo) : null,
            key: 'selection',
        };
    }
    // initialFilters exists (a drill-down handoff) but carries no date — that was deliberate
    // (a snapshot-tile drill-down clears the date range), so stay unbounded instead of
    // re-defaulting to today.
    return { startDate: null, endDate: null, key: 'selection' };
};

const initialAppliedFiltersFor = (initialFilters, defaultDays) => {
    if (initialFilters) return initialFilters;
    const { startDate, endDate } = defaultDateRange(defaultDays);
    return { dateFrom: startDate.toISOString(), dateTo: endDate.toISOString() };
};

// Every pick-list field is a multi-select (an array); Ticket ID and AWB are text.
const EMPTY_FORM_VALUES = { merchant: [], courier: [], ticketId: '', awb: '', category: [], subcategory: [], status: [], assignee: [], severity: [], orderStatus: [], platform: [] };

/** The Merchant field keeps its picks as one comma-joined string (MerchantFilterCached's
 *  outputFormat), the others as arrays; both read back as an array here. */
const picksOf = (value) => (Array.isArray(value) ? value : String(value || '').split(',').filter(Boolean));

/** A filter's picks as an array: its list form if sent, else its single-value form (drill-downs, saved links). */
const listOf = (list, single) => (list && list.length ? list : (single ? [single] : []));

/** The reverse of applyFilters: the form values that send these filters, so a drill-down handed
 *  off from Analytics shows in the bar's fields (and survives the next Filter click). */
const formValuesFor = (filters) => {
    if (!filters) return EMPTY_FORM_VALUES;
    return {
        ...EMPTY_FORM_VALUES,
        merchant: listOf(filters.merchantIds, filters.merchantId),
        courier: filters.courierNames || [],
        ticketId: filters.escalationNumber || '',
        awb: filters.awbNumber || '',
        category: listOf(filters.categories, filters.category),
        subcategory: listOf(filters.subCategories, filters.subCategory),
        status: listOf(filters.statuses, filters.status),
        severity: listOf(filters.severities, filters.severity),
        platform: filters.platforms || [],
        orderStatus: filters.orderStatuses || [],
        assignee: filters.unassignedOnly ? [UNASSIGNED_OPTION] : listOf(filters.assignedToUserIds, filters.assignedToUserId),
    };
};

// Drill-down filters no form field shows (the summary strip highlights its flags; the Tickets page
// lists the rest as "Showing only" chips). Filter keeps them until they're removed there.
// unassignedOnly isn't here: the Assignee field shows it as "Unassigned".
const CARRIED_KEYS = [...DRILLDOWN_FLAG_KEYS, 'closedOnly', 'statusCategory', 'ageFromDays', 'ageToDays']
    .filter((k) => k !== 'unassignedOnly');

/**
 * Shared filter-bar state/wiring for both the Tickets and Analytics pages (Escalation
 * Management V2) — Date Range, Merchant, Courier, Ticket ID, AWB, Category, Subcategory,
 * Status, Assignee, plus the config lookups (statuses/categories/couriers) those fields render from.
 *
 * @param initialFilters optional — pre-seeds appliedFilters/dateRange and the form fields instead
 *   of defaulting to today. Used by the Tickets page to pick up a drill-down handed off from the Analytics page
 *   via the URL query string (see escalationFilterUtils' searchParamsToFilters). Only read once,
 *   on mount — later filter-bar interactions manage their own state independent of the URL.
 * @param options.defaultDays the starting date range when there are no initialFilters: 1 (today,
 *   the Tickets page) or e.g. 30 (Analytics, where one day is too little to show a trend).
 */
export default function useEscalationFilterBar(initialFilters, { defaultDays = 1 } = {}) {
    const { enqueueSnackbar } = useSnackbar();
    const [statusList, setStatusList] = useState([]);
    const [categories, setCategories] = useState([]);
    // null until loaded (or if the caller isn't Prozo staff) — the Assignee field stays hidden.
    const [assigneeOptions, setAssigneeOptions] = useState(null);
    const [orderStatusOptions, setOrderStatusOptions] = useState([]);
    const [dateRange, setDateRange] = useState(() => [initialDateRangeFor(initialFilters, defaultDays)]);
    // The filter values actually sent to the backend — only updated when "Filter"/"Reset" is
    // clicked (or a drill-down is applied), so typing/selecting doesn't re-run the search on
    // every keystroke (matches V1).
    const [appliedFilters, setAppliedFilters] = useState(() => initialAppliedFiltersFor(initialFilters, defaultDays));
    const { data: couriers = [] } = useGetAllCourierPartnersCachedQuery(COURIER_LIST_QUERY_PAYLOAD);

    const filterMethods = useForm({ defaultValues: formValuesFor(initialFilters) });
    const selectedCategories = filterMethods.watch('category');

    // With categories picked, their subcategories; with none, every subcategory. Names that
    // repeat across categories are listed once (filtering by one matches it in each category).
    const subcategoryOptions = useMemo(() => {
        const picked = Array.isArray(selectedCategories) ? selectedCategories : [];
        const source = picked.length ? categories.filter((c) => picked.includes(c.categoryName)) : categories;
        const names = [...new Set(source.flatMap((c) => (c.subCategories || []).map((s) => s.name)))].sort((a, b) => a.localeCompare(b));
        return names.map((name) => ({ value: name, label: name }));
    }, [categories, selectedCategories]);

    // When the categories change, drops picked subcategories that no longer belong to any of
    // them (keeping the rest). A watch-driven effect rather than an onChange on the Category
    // field: the RHF select fields spread their own onChange, which a caller's would override.
    // Skips the first render so a drill-down's subcategory survives mount.
    const isFirstCategoryRender = useRef(true);
    useEffect(() => {
        if (isFirstCategoryRender.current) {
            isFirstCategoryRender.current = false;
            return;
        }
        const allowed = new Set(subcategoryOptions.map((o) => o.value));
        const current = filterMethods.getValues('subcategory') || [];
        const kept = current.filter((s) => allowed.has(s));
        if (kept.length !== current.length) filterMethods.setValue('subcategory', kept);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCategories]);

    useEffect(() => {
        const fetchStatuses = async () => {
            try {
                const response = await cachedGet(apiConstants.ESCALATION_STATUS_CONFIG_V2);
                setStatusList(response?.data || []);
            } catch (error) {
                enqueueSnackbar("Could not load the status list", { variant: "error" });
            }
        };
        const fetchCategories = async () => {
            try {
                const response = await cachedGet(apiConstants.ESCALATION_CATEGORY_CONFIG_V2);
                setCategories(response?.data || []);
            } catch (error) {
                enqueueSnackbar("Could not load the category list", { variant: "error" });
            }
        };
        // People who have tickets assigned to them; quiet on failure, the field just doesn't show.
        const fetchAssignees = async () => {
            try {
                const response = await cachedGet(`${apiConstants.ESCALATION_DETAIL_V2}/assignees`, ASSIGNEE_TTL_MS);
                setAssigneeOptions([
                    { value: UNASSIGNED_OPTION, label: 'Unassigned' },
                    ...(response?.data || []).map((u) => ({ value: u.id, label: u.name })),
                ]);
            } catch (error) {
                setAssigneeOptions(null);
            }
        };
        const fetchOrderStatuses = async () => {
            try {
                const response = await cachedGet(`${apiConstants.ESCALATION_DETAIL_V2}/config/order-statuses`, ASSIGNEE_TTL_MS);
                setOrderStatusOptions((response?.data || []).map((s) => ({ value: s, label: prettyStatus(s) })));
            } catch (error) {
                setOrderStatusOptions([]);
            }
        };
        fetchStatuses();
        fetchCategories();
        fetchAssignees();
        fetchOrderStatuses();
    }, [enqueueSnackbar]);

    const applyFilters = (values) => {
        const { startDate, endDate } = dateRange[0] || {};
        const carried = _.pick(appliedFilters, CARRIED_KEYS);
        const assignees = values.assignee || [];
        // A person picked in the Assignee field replaces a "has an assignee" drill-down.
        if (assignees.length) delete carried.assignedOnly;
        const onlyUnassigned = assignees.length === 1 && assignees[0] === UNASSIGNED_OPTION;
        return {
            ...carried,
            ...(picksOf(values.merchant).length ? { merchantIds: picksOf(values.merchant) } : {}),
            ...(values.courier?.length ? { courierNames: values.courier } : {}),
            ...(values.ticketId ? { escalationNumber: values.ticketId } : {}),
            ...(values.awb ? { awbNumber: values.awb } : {}),
            ...(values.category?.length ? { categories: values.category } : {}),
            ...(values.subcategory?.length ? { subCategories: values.subcategory } : {}),
            ...(values.status?.length ? { statuses: values.status } : {}),
            ...(values.severity?.length ? { severities: values.severity } : {}),
            ...(values.platform?.length ? { platforms: values.platform } : {}),
            ...(values.orderStatus?.length ? { orderStatuses: values.orderStatus } : {}),
            // "Unassigned" alone keeps its drill-down form; mixed with people it's one of the picks
            // (the server reads the UNASSIGNED value as "no assignee").
            ...(onlyUnassigned ? { unassignedOnly: true } : assignees.length ? { assignedToUserIds: assignees } : {}),
            ...(startDate ? { dateFrom: new Date(startDate).toISOString() } : {}),
            ...(endDate ? { dateTo: new Date(new Date(endDate).setHours(23, 59, 59, 999)).toISOString() } : {}),
        };
    };

    // Back to the page's starting point: blank fields (not the drill-down the form was seeded with)
    // and the page's default date range, never an unbounded one.
    const resetFilters = () => {
        filterMethods.reset(EMPTY_FORM_VALUES);
        setDateRange([defaultDateRange(defaultDays)]);
        setAppliedFilters(initialAppliedFiltersFor(undefined, defaultDays));
    };

    const sortedCouriers = useMemo(() => _.orderBy(couriers, "name"), [couriers]);

    return {
        statusList, categories, couriers: sortedCouriers, assigneeOptions, orderStatusOptions,
        dateRange, setDateRange,
        appliedFilters, setAppliedFilters,
        filterMethods, subcategoryOptions,
        applyFilters, resetFilters,
    };
}
